import type { TFunction } from "i18next";
import { mutate } from "swr";

import { EntityAttribute } from "shared/api/api";
import { SortBy } from "shared/types";

import { FilterGroupState } from "features/ui/Filters/FilterBuilder/types";
import { filterBuilderQueryToFilterBuilderState } from "features/ui/Filters/FilterBuilder/utils";

import { listSchemaRecordsRequestURI, SCHEMA_DEFINITIONS_ROUTE } from "./api";
import {
  Column,
  ColumnDataType,
  SchemaDashboardView,
  SchemaDefaultSort,
  SchemaDefinition,
} from "./types";

// Virtual fields plus the columns the schema registry auto-injects when
// smartGrouping / hasAiSummary are enabled. The create endpoint rejects them as
// column names, so mirror the server list here — see
// `schema_registry/v2/model/reserved_names.go` in vehicle-profiles-api.
const RESERVED_COLUMN_NAMES = [
  "ai_summary",
  "attachmentCount",
  "attachments",
  "hasAttachments",
  "mentioned_locations",
  "mentioned_repairs",
  "mentioned_root_causes",
  "mentioned_symptoms",
  "otherCount",
  "search",
  "smart_group_ids",
  "smartGroups",
  "tenant",
];

export const isReservedColumnName = (name: string): boolean =>
  RESERVED_COLUMN_NAMES.includes(name);

// Mirrors validateColumnBasics in vehicle-profiles-api: an identifier of at most 64 characters, and names starting
// with "__" are kept for internal use.
const MAX_COLUMN_NAME_LENGTH = 64;
const COLUMN_NAME_PATTERN = new RegExp(
  `^[A-Za-z_][A-Za-z0-9_]{0,${MAX_COLUMN_NAME_LENGTH - 1}}$`
);

const isValidColumnName = (name: string): boolean =>
  COLUMN_NAME_PATTERN.test(name) && !name.startsWith("__");

export const toValidColumnName = (name: string): string => {
  const replaced = name.trim().replace(/[^A-Za-z0-9_]/g, "_");
  const prefixed = /^[0-9]/.test(replaced) ? `_${replaced}` : replaced;

  const valid = prefixed
    .replace(/^_{2,}/, "_")
    .slice(0, MAX_COLUMN_NAME_LENGTH);

  // A name with nothing left but underscores (e.g. an all-Japanese header) is no use as a default, and several of
  // them would clash, so leave it empty for the user to fill in.
  return /^_*$/.test(valid) ? "" : valid;
};

// The API's column name checks, applied client-side so every column form rejects the same names.
export const getColumnNameError = (
  t: TFunction,
  name: string
): string | null => {
  if (!isValidColumnName(name)) {
    return t("superadmin:data_sources.new.validation.column_name_invalid");
  }

  if (isReservedColumnName(name)) {
    return t("superadmin:data_sources.new.validation.column_name_reserved", {
      name,
    });
  }

  return null;
};

// The columns a table was defined with, in the table's own order. The reserved ones the schema registry adds itself
// (smart_group_ids, mentioned_*, ai_summary, …) are left out: they are neither copied into a new table nor fed.
export const getNonReservedColumns = (columns: Column[]): Column[] =>
  columns
    .filter(({ name }) => !isReservedColumnName(name))
    .sort((a, b) => a.position - b.position);

// Whether a write must supply a value for the column. A required string_array can be left out: ClickHouse arrays are
// never NULL, so an omitted value lands as [] rather than breaking the column's required rule.
export const isValueRequired = (column: Column): boolean =>
  column.isRequired && column.dataType !== "string_array";

export const isStringColumn = (column: Column): boolean =>
  column.dataType === "string";

// Mirrors the backend's allowedOutputColumnDataTypes (excludes `json`).
const GENERIC_ENRICHMENT_OUTPUT_DATA_TYPES = new Set<ColumnDataType>([
  "string",
  "int",
  "float",
  "boolean",
  "date",
  "datetime",
  "datetime_in_utc",
  "string_array",
]);

// Mirrors validateOutputColumn in vehicle-profiles-api.
export const isGenericEnrichmentOutputColumn = (column: Column): boolean =>
  !column.isPrimaryKey &&
  !isReservedColumnName(column.name) &&
  GENERIC_ENRICHMENT_OUTPUT_DATA_TYPES.has(column.dataType);

// Mirrors the backend's validateAllowedOutputLabels.
export const supportsAllowedOutputLabels = (column: Column): boolean =>
  column.dataType === "string" || column.dataType === "string_array";

// Mirrors validateNumOutputValues in vehicle-profiles-api.
export const supportsNumOutputValues = (column: Column): boolean =>
  column.dataType === "string_array";

// Mirrors validateHistoricalPrior in vehicle-profiles-api.
export const supportsHistoricalPrior = (column: Column): boolean =>
  column.dataType === "string";

export const defaultSortToSortBy = (
  defaultSort: SchemaDefaultSort | undefined
): SortBy | undefined =>
  defaultSort ? { [defaultSort.column]: defaultSort.direction } : undefined;

/**
 * Reads a configured default filter (`config.defaultFilters.*`) as builder
 * state. Both shapes the field can hold at runtime are accepted, so this is
 * the one place that has to know the persisted format.
 */
export const getDefaultFilterState = (
  defaultFilter: string | FilterGroupState | null | undefined
): FilterGroupState | undefined =>
  // Transitional, do not drop: configs saved before default filters became
  // query strings still hold builder JSON, which the field's `string` type no
  // longer describes. Those convert on the next edit of the filter.
  defaultFilter && typeof defaultFilter === "object"
    ? defaultFilter
    : filterBuilderQueryToFilterBuilderState(defaultFilter ?? undefined);

export const compareBySidebarOrder = (
  a: SchemaDefinition,
  b: SchemaDefinition
) => (a.config?.sidebarOrder ?? 0) - (b.config?.sidebarOrder ?? 0);

export const getSidebarSchemas = (schemas: SchemaDefinition[]) =>
  schemas
    .filter((s) => s.config?.enableListingPage)
    .sort(compareBySidebarOrder);

export const getVisibleDashboardViews = (
  schema: SchemaDefinition,
  isSuperAdmin: boolean
): SchemaDashboardView[] =>
  (schema.config?.dashboardViews ?? []).filter(
    (view) => !view.beta || isSuperAdmin
  );

// The keyword search is only meaningful when the schema exposes a filterable
// (and not admin-hidden) virtual "search" attribute backed by at least one
// text-searchable string column.
export const supportsKeywordSearch = (attributes: EntityAttribute[]) => {
  const searchAttribute = attributes.find(({ ID }) => ID === "search");

  return (
    Boolean(searchAttribute?.filtering) &&
    !searchAttribute?.hideFilter &&
    attributes.some(
      ({ ID, type, textSearchable }) =>
        ID !== "search" &&
        (type === "string" || type === "array[string]") &&
        textSearchable
    )
  );
};

// Invalidate every cached schema-definition request — the paginated list under all filter/sort/page params, plus
// detail entries. Call after a create or delete so pages that keep their own list (the sidebar, a listing returned
// to) cannot serve a definition that is gone.
//
// `deletedID` names a definition that no longer exists. Its own entries, nested resources included, are dropped
// outright rather than refetched, because those requests would 404 — and SWR keeps unmounted entries, so leaving
// them would resurrect the table on the way Back. Only call this once the page reading them has gone: clearing
// them under a mounted page blanks it to "not found" after a delete that worked.
export const revalidateSchemaDefinitionCaches = (
  deletedID?: string
): Promise<unknown> => {
  const deletedPrefix = deletedID
    ? `${SCHEMA_DEFINITIONS_ROUTE}/${deletedID}`
    : undefined;
  const isDeleted = (key: unknown) =>
    typeof key === "string" &&
    deletedPrefix !== undefined &&
    key.includes(deletedPrefix);

  return Promise.all([
    deletedPrefix !== undefined &&
      mutate(isDeleted, undefined, { revalidate: false }),
    mutate(
      (key) =>
        typeof key === "string" &&
        key.includes(SCHEMA_DEFINITIONS_ROUTE) &&
        !isDeleted(key)
    ),
  ]);
};

// Invalidate every cached read of a table's rows: the listing under any filter or page, the count, and single
// records. Call after the rows were emptied or replaced behind the cache's back. Every one of those requests hangs
// off the listing's own path, which is what the prefix is.
export const revalidateSchemaRecordCaches = ({
  tableType,
  name,
}: Pick<SchemaDefinition, "tableType" | "name">): Promise<unknown> => {
  const prefix = listSchemaRecordsRequestURI({ tableType, name });

  return mutate(
    (key) =>
      typeof key === "string" &&
      (key === prefix ||
        key.startsWith(`${prefix}?`) ||
        key.startsWith(`${prefix}/`))
  );
};
